import { PdfViewer } from '@sequencehq/pdf-viewer'
import { contractFieldsToAnnotations } from './contractFieldsToAnnotations'
import { useAtom } from 'jotai'
import { AssetPdfPreview } from 'Assets/components/AssetPdfPreview'
import { isMatchedFieldPath } from './isMatchedFieldPath'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { ErrorMessage, Center } from '@sequencehq/core-components'
import { ContractField } from '../../../../types'
import { highlightedFieldPathsAtom } from '../../atoms'

export const ContractPreview = ({
  assetId,
  contractFields,
  annotationFieldPathMask
}: {
  assetId: string
  contractFields: ContractField[]
  annotationFieldPathMask: string
}) => {
  const { annotations, visibleAnnotationIds } =
    useContractExtractionAnnotations({
      contractFields,
      annotationFieldPathMask
    })

  return (
    <ErrorBoundary
      name="ContractPreview"
      fallback={
        <Center height="100%">
          <ErrorMessage title="We couldn't load the contract preview" />
        </Center>
      }
    >
      <AssetPdfPreview assetId={assetId}>
        <PdfViewer.Annotations visibleAnnotationIds={visibleAnnotationIds}>
          {annotations.map(annotation => (
            <PdfViewer.Annotation key={annotation.id} {...annotation}>
              <PdfViewer.Annotation.Highlighter
                visibleAnnotationIds={visibleAnnotationIds}
              />
            </PdfViewer.Annotation>
          ))}
        </PdfViewer.Annotations>
      </AssetPdfPreview>
    </ErrorBoundary>
  )
}

const useContractExtractionAnnotations = ({
  contractFields,
  annotationFieldPathMask
}: {
  contractFields: ContractField[]
  annotationFieldPathMask: string
}) => {
  const [highlightedFieldPaths] = useAtom(highlightedFieldPathsAtom)

  const allAnnotations = contractFields
    ? contractFieldsToAnnotations({
        fields: contractFields.filter(field =>
          isMatchedFieldPath({
            fieldPath: field.fieldPath,
            pattern: annotationFieldPathMask
          })
        ),
        referenceCoordinates: {
          width: 1000,
          height: 1000
        }
      })
    : []

  // filter out visible field paths without valid annotations
  const allAnnotationIds = allAnnotations.map(a => a.id)
  const validFieldPathAnnotations = allAnnotationIds.filter(annotationId =>
    highlightedFieldPaths?.some(path =>
      isMatchedFieldPath({
        fieldPath: annotationId,
        pattern: path
      })
    )
  )

  return {
    annotations: allAnnotations,
    visibleAnnotationIds: validFieldPathAnnotations?.length
      ? validFieldPathAnnotations
      : []
  }
}
